import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'

/* ── Types ────────────────────────────────────────────────── */
export type ArcadeEventType = 'points' | 'upset' | 'rank_up' | 'streak'

export interface ArcadeEvent {
  id: string
  type: ArcadeEventType
  title: string       // e.g. "+12 PTS"
  subtitle?: string   // e.g. "Duke defeats UNC"
  icon?: string       // emoji
}

/* ── Singleton event bus ──────────────────────────────────── */
type Listener = (event: ArcadeEvent) => void
const listeners = new Set<Listener>()

export function fireArcadeEvent(event: Omit<ArcadeEvent, 'id'>) {
  const full: ArcadeEvent = { ...event, id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}` }
  listeners.forEach((fn) => fn(full))
}

/* ── Single toast card ────────────────────────────────────── */
const ArcadeToastCard: React.FC<{
  event: ArcadeEvent
  onDone: () => void
}> = ({ event, onDone }) => {
  const [phase, setPhase] = useState<'enter' | 'visible' | 'exit'>('enter')

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('visible'), 50)
    const t2 = setTimeout(() => setPhase('exit'), 2500)
    const t3 = setTimeout(onDone, 3200)
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3) }
  }, [onDone])

  const typeStyles = {
    points: {
      bg: 'from-orange-500/90 to-amber-500/90',
      ring: 'ring-orange-400/50',
      icon: event.icon || '🏀',
    },
    upset: {
      bg: 'from-red-500/90 to-orange-500/90',
      ring: 'ring-red-400/50',
      icon: event.icon || '⚡',
    },
    rank_up: {
      bg: 'from-green-500/90 to-emerald-500/90',
      ring: 'ring-green-400/50',
      icon: event.icon || '📈',
    },
    streak: {
      bg: 'from-purple-500/90 to-blue-500/90',
      ring: 'ring-purple-400/50',
      icon: event.icon || '🔥',
    },
  }

  const style = typeStyles[event.type]

  return (
    <div
      className={clsx(
        'pointer-events-none select-none',
        'transition-all duration-500 ease-out',
        phase === 'enter' && 'opacity-0 scale-50 translate-y-8',
        phase === 'visible' && 'opacity-100 scale-100 translate-y-0',
        phase === 'exit' && 'opacity-0 scale-75 -translate-y-4',
      )}
    >
      <div
        className={clsx(
          'relative px-6 py-4 rounded-2xl',
          'bg-gradient-to-r', style.bg,
          'ring-4', style.ring,
          'shadow-2xl shadow-black/40',
          'backdrop-blur-xl',
          // Subtle shake on upset
          event.type === 'upset' && phase === 'visible' && 'animate-[shake_0.4s_ease-in-out]',
        )}
      >
        {/* Sparkle particles for rank_up */}
        {event.type === 'rank_up' && phase === 'visible' && (
          <>
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1.5 h-1.5 bg-white rounded-full animate-[sparkle_1s_ease-out_forwards]"
                style={{
                  top: '50%',
                  left: '50%',
                  animationDelay: `${i * 0.1}s`,
                  '--sparkle-x': `${(Math.random() - 0.5) * 120}px`,
                  '--sparkle-y': `${(Math.random() - 0.5) * 80}px`,
                } as React.CSSProperties}
              />
            ))}
          </>
        )}

        <div className="flex items-center gap-3">
          <span className="text-3xl">{style.icon}</span>
          <div>
            <div className="text-white font-black text-xl tracking-tight leading-none">
              {event.title}
            </div>
            {event.subtitle && (
              <div className="text-white/70 text-xs font-medium mt-0.5">
                {event.subtitle}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ── Container (portal to body) ───────────────────────────── */
const ArcadeToastContainer: React.FC = () => {
  const [events, setEvents] = useState<ArcadeEvent[]>([])

  useEffect(() => {
    const handler: Listener = (event) => {
      setEvents((prev) => [...prev.slice(-3), event]) // max 4 visible
    }
    listeners.add(handler)
    return () => { listeners.delete(handler) }
  }, [])

  const handleDone = (id: string) => {
    setEvents((prev) => prev.filter((e) => e.id !== id))
  }

  if (events.length === 0) return null

  return createPortal(
    <div className="fixed inset-0 pointer-events-none z-[9999] flex flex-col items-center justify-center gap-3">
      {events.map((event) => (
        <ArcadeToastCard key={event.id} event={event} onDone={() => handleDone(event.id)} />
      ))}
    </div>,
    document.body,
  )
}

export default ArcadeToastContainer
